import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import Logo from './Logo';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const isRTL = i18n.language === 'ar';

  return (
    <header className={`${isRTL ? 'rtl' : ''} bg-primary-900 text-white shadow-lg sticky top-0 z-50`}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center">
            <Link to="/" className="hover:text-accent-400 font-semibold transition">
              {t('nav_home')}
            </Link>
            <Link to="/about" className="hover:text-accent-400 font-semibold transition">
              {t('nav_about')}
            </Link>
            <Link to="/destinations" className="hover:text-accent-400 font-semibold transition">
              {t('nav_destinations')}
            </Link>
            <Link to="/schedule" className="hover:text-accent-400 font-semibold transition">
              {t('nav_schedule')}
            </Link>
            <Link to="/booking" className="hover:text-accent-400 font-semibold transition">
              {t('nav_booking')}
            </Link>
            <Link to="/contact" className="hover:text-accent-400 font-semibold transition">
              {t('nav_contact')}
            </Link>
            <Link to="/suggestion" className="hover:text-accent-400 font-semibold transition">
              {t('nav_suggestion')}
            </Link>
          </nav>

          {/* Language Switcher */}
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => changeLanguage('fr')}
              className={`px-3 py-1 rounded-lg font-semibold text-sm transition ${
                i18n.language === 'fr' ? 'bg-accent-500 text-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              FR
            </button>
            <button
              onClick={() => changeLanguage('ar')}
              className={`px-3 py-1 rounded-lg font-semibold text-sm transition ${
                i18n.language === 'ar' ? 'bg-accent-500 text-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              العربية
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            ref={hamburgerRef}
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition"
            aria-label="Toggle menu"
          >
            <div className="space-y-1.5">
              <span className={`block w-6 h-0.5 bg-white transition ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-white transition ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-white transition ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className="md:hidden absolute left-0 right-0 top-full bg-primary-900 shadow-2xl pb-4 pt-2 z-40"
          >
            <nav className={`flex flex-col ${isRTL ? 'items-end pr-4' : 'items-start pl-4'} space-y-3`}>
              <Link
                to="/"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full border-b border-white/10"
              >
                {t('nav_home')}
              </Link>
              <Link
                to="/about"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full border-b border-white/10"
              >
                {t('nav_about')}
              </Link>
              <Link
                to="/destinations"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full border-b border-white/10"
              >
                {t('nav_destinations')}
              </Link>
              <Link
                to="/schedule"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full border-b border-white/10"
              >
                {t('nav_schedule')}
              </Link>
              <Link
                to="/booking"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full border-b border-white/10"
              >
                {t('nav_booking')}
              </Link>
              <Link
                to="/contact"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full border-b border-white/10"
              >
                {t('nav_contact')}
              </Link>
              <Link
                to="/suggestion"
                onClick={handleNavClick}
                className="py-2 hover:text-accent-400 font-semibold transition w-full"
              >
                {t('nav_suggestion')}
              </Link>
            </nav>

            {/* Mobile Language Switcher */}
            <div className={`flex gap-2 mt-4 ${isRTL ? 'pr-4 justify-end' : 'pl-4'}`}>
              <button
                onClick={() => changeLanguage('fr')}
                className={`px-3 py-1 rounded-lg font-semibold text-sm transition ${
                  i18n.language === 'fr' ? 'bg-accent-500 text-white' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => changeLanguage('ar')}
                className={`px-3 py-1 rounded-lg font-semibold text-sm transition ${
                  i18n.language === 'ar' ? 'bg-accent-500 text-white' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                العربية
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}